'use client'

import React, { useMemo } from 'react'
import { Box, Typography, Slider, TextField, Stack } from '@mui/material'

interface IFilterOption {
  label: string
  value: string
  count?: number
}

interface PriceFilterProps {
  attributeCode: string
  label: string
  icon?: string
  options: IFilterOption[]
  priceRange: [number, number]
  onChange: (attributeCode: string, newValue: number | number[]) => void
  onInputChange: (attributeCode: string, type: 'min' | 'max', value: string) => void
}

/**
 * Component filter dạng price range slider
 * Parse min-max từ options của aggregations
 */
export const PriceFilter: React.FC<PriceFilterProps> = ({
  attributeCode,
  label,
  icon,
  options,
  priceRange,
  onChange,
  onInputChange,
}) => {
  const formatPrice = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    }
    if (value >= 1000) {
      return `${Math.round(value / 1000)}k`
    }
    return value.toString()
  }
  // Parse min-max từ options (format: "480000_480010")
  const { minPrice, maxPrice, marks } = useMemo(() => {
    if (!options || options.length === 0) {
      return { minPrice: 0, maxPrice: 0, marks: [] }
    }

    let min = Infinity
    let max = -Infinity

    options.forEach((option) => {
      const [minVal, maxVal] = option.value.split('_').map(Number)
      if (minVal < min) min = minVal
      if (maxVal > max) max = maxVal
    })

    // Tạo marks cho slider (4 điểm)
    const step = (max - min) / 3
    const generatedMarks = [
      { value: min, label: formatPrice(min) },
      { value: Math.round(min + step), label: formatPrice(Math.round(min + step)) },
      { value: Math.round(min + step * 2), label: formatPrice(Math.round(min + step * 2)) },
      { value: max, label: formatPrice(max) },
    ]

    return { minPrice: min, maxPrice: max, marks: generatedMarks }
  }, [options])

  const formatPriceInput = (value: number) => {
    return new Intl.NumberFormat('vi-VN').format(value)
  }

  return (
    <Stack gap={1} sx={{ mb: 1 }}>
      <Typography
        sx={{
          fontSize: '18px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        {icon && <Box component="span">{icon}</Box>}
        {label}
      </Typography>

      {/* Slider */}
      <Box sx={{ px: 2, mb: 1.5 }}>
        <Slider
          value={priceRange}
          onChange={(_, value) => onChange(attributeCode, value as number[])}
          valueLabelDisplay="auto"
          valueLabelFormat={formatPriceInput}
          min={minPrice}
          max={maxPrice}
          step={50000}
          marks={marks}
          slotProps={{
            thumb: {
              style: { width: 16, height: 16 },
            },
            rail: {
              style: { height: 8 },
            },
            track: { style: { height: 8 } },
            mark: { style: { height: 8, top: '90%', width: 1, backgroundColor: '#BBBBBA' } },
          }}
        />
      </Box>

      {/* Input fields */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 300,
              color: 'primary.main',
              mb: 0.5,
            }}
          >
            Giá từ:
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={formatPriceInput(priceRange[0])}
            onChange={(e) => onInputChange(attributeCode, 'min', e.target.value)}
            InputProps={{
              endAdornment: (
                <Typography sx={{ fontSize: '14px', fontWeight: 300, color: 'primary.main' }}>Đ</Typography>
              ),
              sx: {
                fontSize: '14px',
                fontWeight: 300,
                borderRadius: '8px',
                height: 40,
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'grey.500',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 300,
              color: 'primary.main',
              mb: 0.5,
            }}
          >
            Đến:
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={formatPriceInput(priceRange[1])}
            onChange={(e) => onInputChange(attributeCode, 'max', e.target.value)}
            InputProps={{
              endAdornment: (
                <Typography sx={{ fontSize: '14px', fontWeight: 300, color: 'primary.main' }}>Đ</Typography>
              ),
              sx: {
                fontSize: '14px',
                fontWeight: 300,
                borderRadius: '8px',
                height: 40,
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'grey.500',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Stack>
  )
}
